// axios配置  可自行根据项目进行更改，只需更改该文件即可，其他文件可以不动
// The axios configuration can be changed according to the project, just change the file, other files can be left unchanged

import type { AxiosResponse } from 'axios'
import { clone } from 'lodash-es'
import type { RequestOptions, Result } from '/#/axios'
import fileDownload from 'js-file-download'
import type { AxiosTransform, CreateAxiosOptions } from './axiosTransform'
import { VAxios } from './Axios'
import { checkStatus } from './checkStatus'
import { useGlobSetting } from '/@/hooks/setting'
import { useMessage } from '/@/hooks/web/useMessage'
import { ContentTypeEnum, RequestEnum } from '/@/enums/httpEnum'
import { isString } from '/@/utils/is'
import { getToken } from '/@/utils/auth'
import { deepMerge, setObjToUrlParams } from '/@/utils'
import { useErrorLogStoreWithOut } from '/@/store/modules/errorLog'
import { useI18n } from '/@/hooks/web/useI18n'
import { formatRequestDate, joinTimestamp } from './helper'
import { useUserStoreWithOut } from '/@/store/modules/user'
import { AxiosRetry } from '/@/utils/http/axios/axiosRetry'
import * as HTTP from '/@/api/types'
import { API_URL, API_URL_PREFIX } from '/@/utils/net'
import { handleResponseResource } from '/@/utils/proxy'
import { router } from '/@/router'
import { PageEnum } from '/@/enums/pageEnum'

const GLOBAL_API_ERROR_MESSAGE_KEY = 'GLOBAL_MESSAGE_KEY'
const globSetting = useGlobSetting()
const urlPrefix = globSetting.urlPrefix
const { createMessage, createErrorModal } = useMessage()

/**
 * @description: 数据处理，方便区分多种处理方式
 */
const transform: AxiosTransform = {
    /**
     * @description: 处理请求数据。如果数据不是预期格式，可直接抛出错误
     */
    transformRequestHook: (res: AxiosResponse<Result>, options: RequestOptions) => {
        const { t } = useI18n()
        const { isTransformResponse, isReturnNativeResponse, download } = options

        // 判断是否为下载
        const disposition = res.headers['content-disposition']
        if (download && disposition) {
            const fileName = decodeURIComponent(disposition.split('=')[1])
            fileDownload(res.data, fileName)
            return
        }

        // 是否返回原生响应头 比如：需要获取响应头时使用该属性
        if (isReturnNativeResponse) {
            return res
        }
        // 不进行任何处理，直接返回
        // 用于页面代码可能需要直接获取code，data，message这些信息时开启
        if (!isTransformResponse) {
            return res.data
        }
        // 错误的时候返回

        const { data } = res
        if (!data) {
            // return '[HTTP] Request has no return value';
            throw new Error(t('sys.api.apiRequestFailed'))
        }
        //  这里 status，body，message为 后台统一的字段，需要在 types.ts 内修改为项目自己的接口返回格式
        const { status, body, message, details } = data

        // 这里逻辑可以根据项目进行修改
        const hasSuccess = data && Reflect.has(data, 'status') && status === HTTP.Status.OK
        if (hasSuccess) {
            return handleResponseResource(body, options)
        }

        // 在此处根据自己项目的实际情况对不同的code执行不同的操作
        // 如果不希望中断当前请求，请return数据，否则直接抛出异常即可
        let timeoutMsg = ''
        console.log('status', status)

        switch (status) {
            case HTTP.Status.UNAUTHORIZED: {
                const urlQuery = router.currentRoute.value?.query
                if (urlQuery.token !== undefined) {
                    // 大屏iframe用户跳转异常提示页
                    // userStore.logout(false)
                    router.push(`${PageEnum.ifram_ERROR_PAGE}?token=${urlQuery.token}`)
                } else {
                    timeoutMsg = t('sys.api.timeoutMessage')
                    const userStore = useUserStoreWithOut()
                    userStore.setToken(undefined)
                    userStore.logout(true)
                }
                break
            }
            default: {
                if (details) {
                    timeoutMsg = details
                } else if (message) {
                    timeoutMsg = message
                }
            }
        }

        // errorMessageMode=‘modal’的时候会显示modal错误弹窗，而不是消息提示，用于一些比较重要的错误
        // errorMessageMode='none' 一般是调用时明确表示不希望自动弹出错误提示
        if (options.errorMessageMode === 'modal') {
            createErrorModal({ title: t('sys.api.errorTip'), content: timeoutMsg })
        } else if (options.errorMessageMode === 'message') {
            createMessage.error({ content: timeoutMsg, key: GLOBAL_API_ERROR_MESSAGE_KEY })
        }

        throw new Error(timeoutMsg || t('sys.api.apiRequestFailed'))
    },

    // 请求之前处理config
    beforeRequestHook: (config, options) => {
        const { apiUrl, joinPrefix, joinParamsToUrl, formatDate, joinTime = true, urlPrefix } = options

        if (joinPrefix) {
            config.url = `${urlPrefix}${config.url}`
        }

        if (apiUrl && isString(apiUrl)) {
            config.url = `${apiUrl}${config.url}`
        }
        const params = config.params || {}
        const data = config.data || false
        formatDate && data && !isString(data) && formatRequestDate(data)
        if (config.method?.toUpperCase() === RequestEnum.GET) {
            if (!isString(params)) {
                // 给 get 请求加上时间戳参数，避免从缓存中拿数据。
                config.params = Object.assign(params || {}, joinTimestamp(joinTime, false))
            } else {
                // 兼容restful风格
                config.url = `${config.url + params}${joinTimestamp(joinTime, true)}`
                config.params = undefined
            }
        } else {
            if (!isString(params)) {
                formatDate && formatRequestDate(params)
                if (Reflect.has(config, 'data') && config.data && Object.keys(config.data).length > 0) {
                    config.data = data
                    config.params = params
                } else {
                    // 非GET请求如果没有提供data，则将params视为data
                    config.data = params
                    config.params = undefined
                }
                if (joinParamsToUrl) {
                    config.url = setObjToUrlParams(config.url as string, Object.assign({}, config.params, config.data))
                }
            } else {
                // 兼容restful风格
                config.url = config.url + params
                config.params = undefined
            }
        }
        return config
    },

    /**
     * @description: 请求拦截器处理
     */
    requestInterceptors: (config, options) => {
        // iframe大屏 urltoken 带上时，直接使用url中token
        let token = getToken()
        const urlToken = router.currentRoute?.value?.query?.token
        // const urlToken = config?.params?.urlToken
        // console.log('urlToken', urlToken)

        if (urlToken) {
            token = urlToken
        }
        // 请求之前处理config
        // const token = getToken()
        if (token && (config as Recordable)?.requestOptions?.withToken !== false) {
            // jwt token
            ;(config as Recordable).headers['App-Token'] = options.authenticationScheme
                ? `${options.authenticationScheme} ${token}`
                : token
        }
        return config
    },

    /**
     * @description: 响应拦截器处理
     */
    responseInterceptors: (res: AxiosResponse<any>) => {
        return res
    },

    /**
     * @description: 响应错误处理
     */
    responseInterceptorsCatch: (axiosInstance: AxiosResponse, error: any) => {
        const { t } = useI18n()
        const errorLogStore = useErrorLogStoreWithOut()
        errorLogStore.addAjaxErrorInfo(error)
        const { response, code, message, config } = error || {}
        const errorMessageMode = config?.requestOptions?.errorMessageMode || 'none'
        const msg: string = response?.data?.error?.message ?? ''
        const err: string = error?.toString?.() ?? ''
        let errMessage = ''

        try {
            if (code === 'ECONNABORTED' && message.includes('timeout')) {
                errMessage = t('sys.api.apiTimeoutMessage')
            }
            if (err?.includes('Network Error')) {
                errMessage = t('sys.api.networkExceptionMsg')
            }

            if (errMessage) {
                if (errorMessageMode === 'modal') {
                    createErrorModal({ title: t('sys.api.errorTip'), content: errMessage })
                } else if (errorMessageMode === 'message') {
                    createMessage.error({ content: errMessage, key: GLOBAL_API_ERROR_MESSAGE_KEY })
                }
                return Promise.reject(error)
            }
        } catch (error) {
            throw new Error(error as unknown as string)
        }

        checkStatus(error?.response?.status, msg, errorMessageMode)

        // 添加自动重试机制 保险起见 只针对GET请求
        const retryRequest = new AxiosRetry()
        const { isOpenRetry } = config.requestOptions.retryRequest
        config.method?.toUpperCase() === RequestEnum.GET &&
            isOpenRetry &&
            // @ts-expect-error
            retryRequest.retry(axiosInstance, error)
        return Promise.reject(error)
    },
}

function createAxios(opt?: Partial<CreateAxiosOptions>) {
    return new VAxios(
        deepMerge(
            {
                // See https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#authentication_schemes
                // authentication schemes，e.g: Bearer
                // authenticationScheme: 'Bearer',
                authenticationScheme: '',
                timeout: 30 * 1000,
                // 基础接口地址
                // baseURL: globSetting.apiUrl,

                // headers: { 'Content-Type': ContentTypeEnum.JSON },
                // 如果是form-data格式
                headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
                // 数据处理方式
                transform: clone(transform),
                // 配置项，下面的选项都可以在独立的接口请求中覆盖
                requestOptions: {
                    // 默认将prefix 添加到url
                    joinPrefix: true,
                    // 是否返回原生响应头 比如：需要获取响应头时使用该属性
                    isReturnNativeResponse: false,
                    // 需要对返回数据进行处理
                    isTransformResponse: true,
                    // post请求的时候添加参数到url
                    joinParamsToUrl: false,
                    // 格式化提交参数时间
                    formatDate: true,
                    // 消息提示类型
                    errorMessageMode: 'message',
                    // 接口地址
                    apiUrl: globSetting.apiUrl,
                    // 接口拼接地址
                    urlPrefix,
                    //  是否加入时间戳
                    joinTime: true,
                    // 忽略重复请求
                    ignoreCancelToken: true,
                    // 是否携带token
                    withToken: true,
                    retryRequest: {
                        isOpenRetry: false,
                        count: 5,
                        waitTime: 100,
                    },
                },
            },
            opt || {},
        ),
    )
}

// TODO: 实际项目所需的请求配置，可自定义扩展
export const defHttp = createAxios({
    requestOptions: {
        apiUrl: API_URL,
        urlPrefix: API_URL_PREFIX,
    },
})

export const testHttp = createAxios({
    headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
    requestOptions: {
        apiUrl: API_URL,
        urlPrefix: API_URL_PREFIX,
    },
})

// other api url
export const otherHttp = createAxios({
    headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
    requestOptions: {
        apiUrl: API_URL,
        urlPrefix: API_URL_PREFIX,
    },
})

export const downloadHttp = createAxios({
    timeout: 0,
    headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
    requestOptions: {
        apiUrl: API_URL,
        urlPrefix: API_URL_PREFIX,
        // 后台返回的data为blob类型，不需要对返回数据进行处理
        isReturnNativeResponse: true,
    },
})

/**
 * 用于请求JSON文件
 */
export const httplocal = createAxios({
    requestOptions: {
        urlPrefix: '',
        apiUrl: '',
        withToken: false,
        isTransformResponse: false,
        joinTime: false,
    },
})

/**
 * 用于请求后台代理的第三方接口（不经过响应数据格式转换）
 */
export const rawHttp = createAxios({
    requestOptions: {
        apiUrl: API_URL,
        urlPrefix: API_URL_PREFIX,
        withToken: false,
        isTransformResponse: false,
        joinTime: false,
    },
})
